import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { useFloatingDropdownPanel } from '@/storybook/components/FormCollection/components/DropdownList/useFloatingDropdownPanel';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import { buildAvailableValueSet, formatActiveTotalBadge, getActiveTotalCount } from '@/utils/filter/cascadingFilters';
import cx from 'classnames';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import TreeSelectRows from './Components/TreeSelectRows/TreeSelectRows';
import { TreeSelectProps } from './TreeSelect.types';
import styles from './TreeSelectFloatingLabel.module.css';
import { useTreeSelect } from './hooks/useTreeSelect';

export type TreeSelectFloatingLabelProps<T> = Omit<TreeSelectProps<T>, 'label'> & {
  label?: ReactNode;
  state?: 'default' | 'filled' | 'error';
};

export default function TreeSelectFloatingLabel<T extends string>({
  value = [],
  onChange,
  options,
  isLoading,
  placeholder,
  searchable,
  disabled = false,
  label,
  tooltip,
  alignment = 'vertical',
  adobeEventTrackFunctions,
  onDropdownToggle,
  'data-testid': dataTestId,
  'dropdownLabelTestId': _dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  className,
  dropdownButtonClassName,
  collapseSingleChild,
  selectionMode = 'standard',
  size = 'L',
  state = 'default',
  hideCheckboxes = false,
  clearable = true,
  nestedKeySort = 'alphabetical',
  actionBarProps,
}: TreeSelectFloatingLabelProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');

  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const { dropdownRef, panelRef } = useFloatingDropdownPanel({
    isOpen,
    onClose: () => {
      if (isOpen) onDropdownToggle?.(false);
      setIsOpen(false);
    },
  });

  useScrollIntoView({ elementRef: dropdownRef, isVisible: isOpen });

  const {
    fixedOptions,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    visibleRows,
    renderTreeRow,
    virtualization,
  } = useTreeSelect<T>({
    options,
    value,
    onChange,
    collapseSingleChild,
    selectionMode,
    nestedKeySort,
    hideCheckboxes,
    disabled,
    treeListItemClassName: styles.treeListItem,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onDropdownToggle?.(newState);
    if (newState) {
      handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
      setSearchInput('');
    }
  }, [disabled, isOpen, dropdownPosition, dropdownRef, onDropdownToggle, setSearchInput]);

  const handleOnClearValue = useCallback(() => {
    if (!disabled) onChange([]);
  }, [disabled, onChange]);

  // Cascading filters: when tree options carry disabled flags, show `{active}/{total}`
  // so the trigger badge reflects selections that are no longer available under the
  // current global-filter combination instead of the raw selection count.
  const countLabel = useMemo(() => {
    const hasCascadingState = options?.some(option => option.disabled != null) ?? false;
    if (!hasCascadingState) return undefined;
    const availableValues = buildAvailableValueSet(options?.filter(option => option.disabled !== true));
    return formatActiveTotalBadge(getActiveTotalCount(value, availableValues)) ?? undefined;
  }, [options, value]);

  const displayLabel = label || placeholder;
  const isLabelFloating = value.length > 0 || isOpen;

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.floatingLabelContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
      data-state={state}
    >
      <section className={styles.dropdownContainer}>
        {displayLabel && (
          <label className={styles.floatingLabel} data-floating={isLabelFloating} data-has-value={value.length > 0}>
            {displayLabel}
          </label>
        )}
        <div className={styles.buttonWrapper}>
          <DropdownButton
            ref={dropdownButtonRef}
            type="multiSelect"
            size={size}
            className={dropdownButtonClassName}
            placeholder=""
            numOfValue={value?.length}
            active={isOpen}
            onClick={!disabled ? handleToggleDropdown : undefined}
            onClear={handleOnClearValue}
            clearable={!hideCheckboxes && clearable}
            disabled={disabled}
            countLabel={countLabel}
            data-testid={dropdownButtonTestId}
          >
            <div className={styles.value}>
              <span className={styles.valueText}>{valueLabelList.join(', ')}</span>
            </div>
          </DropdownButton>
        </div>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={disabled}
          position={dropdownListPosition}
          isSearchable={searchable}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchInput ? searchResults.size : fixedOptions.length}
          data-testid={dropdownListTestId}
          listRef={virtualization.parentRef}
          dropdownTriggerRef={dropdownButtonRef}
          isFloating
          panelRef={panelRef}
          optionsCount={options.length}
          actionBarProps={actionBarProps}
        >
          <div className={styles.treeSelectList}>
            <TreeSelectRows visibleRows={visibleRows} renderTreeRow={renderTreeRow} virtualization={virtualization} />
          </div>
        </DropdownList>
      </section>
      {tooltip && <div className={styles.tooltip}>{tooltip}</div>}
    </div>
  );
}
